import { useState } from "react";
import { PASS_THRESHOLD, SECTION_META } from "../data/index";
import QuestionCard from "./QuestionCard";

export default function ResultsScreen({ section, answers, questions, mode, onRetry, onHome, onGoHistory }) {
  const [filter, setFilter] = useState("incorrect"); // default: show wrong answers first

  const correct   = questions.filter((q, i) => answers[i] === q.answer).length;
  const total     = questions.length;
  const unanswered = total - Object.keys(answers).length;
  const incorrect  = total - correct - unanswered;
  const pct       = Math.round((correct / total) * 100);
  const passed    = pct >= PASS_THRESHOLD;
  const meta      = SECTION_META[section] ?? { icon: "📋", color: "#374151", lightColor: "#f9fafb", borderColor: "#e5e7eb" };
  const isExam    = mode === "exam";

  const counts = { all: total, incorrect, correct, skipped: unanswered };

  const filtered = questions.filter((q, i) => {
    const isCorrect = answers[i] === q.answer;
    const skipped   = answers[i] === undefined;
    if (filter === "incorrect") return !isCorrect && !skipped;
    if (filter === "correct")   return isCorrect;
    if (filter === "skipped")   return skipped;
    return true; // "all"
  });

  return (
    <div className="results-screen">

      {/* ── SCORE HEADER ───────────────────────────────────────────── */}
      <div className="results-top" style={{ background: meta.lightColor, borderColor: meta.borderColor }}>
        <div className={`results-badge ${passed ? "pass" : "fail"}`}>
          {passed ? "✓ PASS" : "✗ FAIL"}
        </div>
        <div className="results-section-info">
          <span className="results-icon">{meta.icon}</span>
          <span className="results-section-name" style={{ color: meta.color }}>{section}</span>
          <span className={`results-mode-pill ${isExam ? "exam-pill" : "practice-pill"}`}>
            {isExam ? "🎯 Exam" : "📚 Practice"}
          </span>
        </div>
      </div>

      <div className="results-score-row">
        <div className="results-score-big" style={{ color: passed ? "#16a34a" : "#dc2626" }}>{pct}%</div>
        <div className="results-score-detail">
          <div><strong style={{ color: "#16a34a" }}>{correct}</strong> correct</div>
          <div><strong style={{ color: "#dc2626" }}>{incorrect}</strong> incorrect</div>
          {unanswered > 0 && <div><strong>{unanswered}</strong> skipped</div>}
          <div>out of <strong>{total}</strong> total</div>
        </div>
      </div>

      <p className={`results-verdict ${passed ? "pass-text" : "fail-text"}`}>
        {passed
          ? "Great work! You're on track for your CDL exam."
          : `You need ${PASS_THRESHOLD}% to pass. Review the wrong answers below and try again!`}
      </p>

      {/* ── PROGRESS BAR ───────────────────────────────────────────── */}
      <div className="results-gauge">
        <div className="gauge-bar">
          <div className="gauge-fill" style={{ width: `${pct}%`, background: passed ? "#16a34a" : "#dc2626" }} />
          <div className="gauge-threshold" style={{ left: `${PASS_THRESHOLD}%` }} title="Pass threshold (80%)" />
        </div>
        <div className="gauge-labels">
          <span>0%</span>
          <span style={{ marginLeft: `${PASS_THRESHOLD - 2}%` }}>80%</span>
          <span style={{ marginLeft: "auto" }}>100%</span>
        </div>
      </div>

      {/* ── QUESTION GRID ──────────────────────────────────────────── */}
      <div className="results-breakdown">
        <h4>Question-by-Question Breakdown</h4>
        <div className="breakdown-grid">
          {questions.map((q, i) => {
            const isCorrect = answers[i] === q.answer;
            const skipped   = answers[i] === undefined;
            return (
              <div
                key={q.id}
                className={`breakdown-item ${skipped ? "bk-skipped" : isCorrect ? "bk-correct" : "bk-incorrect"}`}
                title={`Q${i + 1}: ${skipped ? "Skipped" : isCorrect ? "Correct" : "Incorrect"}`}
                onClick={() => {
                  setFilter("all");
                  setTimeout(() => {
                    document.getElementById(`review-q-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="bk-num">{i + 1}</span>
                <span className="bk-icon">{skipped ? "—" : isCorrect ? "✓" : "✗"}</span>
              </div>
            );
          })}
        </div>
        <p className="breakdown-hint">Click any square to jump to that question below.</p>
      </div>

      {/* ── ACTION BUTTONS ─────────────────────────────────────────── */}
      <div className="results-actions">
        <button className="btn btn-secondary" onClick={onRetry}>
          {isExam ? "🎯 New Exam (New Questions)" : "🔄 Retry Practice"}
        </button>
        <button className="btn btn-outline" onClick={onHome}>← Home</button>
        {onGoHistory && <button className="btn btn-outline" onClick={onGoHistory}>📜 History</button>}
      </div>

      {/* ── INLINE ANSWER REVIEW ───────────────────────────────────── */}
      <div className="inline-review">
        <div className="inline-review-header">
          <h3>📋 Answer Review</h3>
          <p className="inline-review-sub">See every question with the correct answer and explanation.</p>
        </div>

        {/* Filter tabs */}
        <div className="review-filters">
          {[
            { key: "incorrect", label: "✗ Wrong",   count: counts.incorrect, color: "#dc2626" },
            { key: "correct",   label: "✓ Right",   count: counts.correct,   color: "#16a34a" },
            { key: "skipped",   label: "— Skipped", count: counts.skipped,   color: "#9ca3af" },
            { key: "all",       label: "All",        count: counts.all,       color: "#1a56db" },
          ].map(({ key, label, count, color }) => (
            <button
              key={key}
              className={`filter-btn ${filter === key ? "filter-active" : ""}`}
              style={filter === key ? { background: color, borderColor: color } : {}}
              onClick={() => setFilter(key)}
            >
              {label} <span className="filter-count">{count}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="empty-filter">
            {filter === "incorrect" ? "🎉 No wrong answers!" :
             filter === "skipped"   ? "✓ No skipped questions." :
                                      "No questions in this category."}
          </div>
        ) : (
          <div className="review-list">
            {filtered.map((q) => {
              const originalIdx = questions.indexOf(q);
              return (
                <div key={q.id} id={`review-q-${originalIdx}`}>
                  <QuestionCard
                    question={q}
                    index={originalIdx}
                    total={questions.length}
                    selected={answers[originalIdx] ?? -1}
                    onSelect={() => {}}
                    showAnswer={true}
                    flagged={false}
                    onFlag={() => {}}
                    isExam={false}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom retry button */}
        <div className="results-actions" style={{ marginTop: "24px" }}>
          <button className="btn btn-secondary" onClick={onRetry}>
            {isExam ? "🎯 New Exam (New Questions)" : "🔄 Retry Practice"}
          </button>
          <button className="btn btn-outline" onClick={onHome}>← Home</button>
          {onGoHistory && <button className="btn btn-outline" onClick={onGoHistory}>📜 History</button>}
        </div>
      </div>

    </div>
  );
}
